// "Compra de la semana" en Compras y Almacén: une la compra de varios eventos
// (decisión del usuario 05-oct-2026: va aquí, no en el detalle del presupuesto).
//
// Rango de fechas (por defecto de hoy a hoy + 7), eventos confirmados o
// aceptados del rango con casillas (todos marcados) y la tabla combinada con el
// mismo formato compacto que la pestaña del presupuesto: cada ingrediente
// sumado con el stock y las reservas contadas una sola vez y lo ya pedido
// restado. El estado (pendiente / pedido / comprado) se cambia por línea o por
// proveedor ("Todo pedido", "Todo comprado") y se aplica a cada evento del
// rango; los totales por estado de arriba filtran la tabla.
// "No pedir" por ingrediente (se propaga a la línea de cada evento del rango)
// y paquetes editables (se guardan solo para este rango de fechas).
// PDF y WhatsApp de la lista y, en cada proveedor, "PDF del pedido" y
// "WhatsApp del pedido" con la fecha de entrega; después, marcar sus líneas
// como pedidas en cada evento (sin orden). La orden de compra para todos los
// eventos queda oculta (`MOSTRAR_CREAR_ORDEN`).
//
// Aviso de pedidos cortos (05-oct-2026): franja arriba con los pedidos ya
// hechos que se han quedado cortos (o sobran) por eventos nuevos o cambiados
// en los días pedidos; "Ver la diferencia" filtra por el proveedor con "Solo
// lo que falta". La URL puede traer `desde`, `hasta`, `proveedor` y
// `solo_falta=1` (botón del aviso al entrar). Con "Solo lo que falta", el PDF y
// el WhatsApp del proveedor llevan solo la diferencia (pedido complementario)
// y marcarlo como pedido la suma a lo ya pedido.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { listaCompraV2API } from '../../api/api';
import { useVistaEmpleado } from '../../context/VistaEmpleadoContext';
import { useToast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';
import { hoyMadrid } from '../../utils/fechas';
import TablaListaCompra from './TablaListaCompra';
import ConfirmarPedido from './ConfirmarPedido';
import AvisosPedidosFranja from './AvisosPedidosFranja';
import IngredienteModal from '../ingredientes/IngredienteModal';
import {
  MOSTRAR_CREAR_ORDEN, TONO_ESTADO, compartirTextoMovil, copiarTexto, ddmm, descargarRespuesta, formatEuros, leerParametrosCompraSemana, vispera,
} from './listaCompraUtils';

const claseBoton = 'h-10 sm:h-[34px] px-3 rounded-lg border text-[13px] font-bold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const botonPrincipal = `${claseBoton} bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90`;
const botonSecundario = `${claseBoton} bg-white border-[#CBD3CF] text-[#16221D] hover:bg-gray-50`;
const ESTADOS_SEMANA = ['pendiente', 'pedido', 'comprado'];
const claseInput = 'h-10 sm:h-[34px] px-2 text-sm border border-[#CBD3CF] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-duetto-primary';

// ¿Puede abrir el detalle del presupuesto? Misma regla que checkRouteAccess de
// App.js para '/presupuestos' (no se importa de App.js para no crear un ciclo):
// dirección siempre; con permisos_modulos, que incluya 'presupuestos'; sin
// ellos, por rol. Sin acceso, el enlace llevaba al calendario sin avisar.
const ROLES_PRESUPUESTOS = ['direccion', 'comercial', 'encargado'];
const puedeAbrirPresupuesto = (user) => {
  if (!user) return false;
  if (user.rol === 'direccion') return true;
  if (Array.isArray(user.permisos_modulos) && user.permisos_modulos.length > 0) {
    return user.permisos_modulos.includes('presupuestos');
  }
  return ROLES_PRESUPUESTOS.includes(user.rol);
};

const sumarDiasIso = (iso, dias) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const CompraSemana = () => {
  const { toast } = useToast();
  // En "Simular vista empleado" decide con el usuario simulado
  const { usuarioEfectivo } = useVistaEmpleado();
  const verPresupuesto = puedeAbrirPresupuesto(usuarioEfectivo);
  const location = useLocation();
  const inicial = useMemo(() => leerParametrosCompraSemana(location.search), [location.search]);
  const [desde, setDesde] = useState(() => inicial.desde || hoyMadrid());
  const [hasta, setHasta] = useState(() => inicial.hasta || sumarDiasIso(inicial.desde || hoyMadrid(), 7));
  const [seleccion, setSeleccion] = useState(null); // null = todos los eventos del rango
  const [eventos, setEventos] = useState([]);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [agrupacion, setAgrupacion] = useState('proveedor');
  const [soloFalta, setSoloFalta] = useState(() => inicial.soloFalta);
  const [proveedorExport, setProveedorExport] = useState(() => inicial.proveedor || '');
  const [proveedorFiltro, setProveedorFiltro] = useState(() => inicial.proveedor || ''); // "Ver la diferencia"
  const [avisos, setAvisos] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [pedido, setPedido] = useState(null); // {grupo, creando, resultado}
  const [ingredienteAbierto, setIngredienteAbierto] = useState(null);
  const [guardando, setGuardando] = useState({}); // {clave: true}
  const [estadoFiltro, setEstadoFiltro] = useState(''); // chip de totales pulsado ('' = todas)

  const ninguno = Array.isArray(seleccion) && seleccion.length === 0;

  const cargar = useCallback(async () => {
    if (!desde || !hasta) return;
    setCargando(true);
    try {
      const { data } = await listaCompraV2API.combinada({
        desde, hasta, presupuestoIds: Array.isArray(seleccion) && seleccion.length > 0 ? seleccion : null, soloFalta,
      });
      setEventos(data?.eventos || []);
      setDatos(data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo calcular la compra de la semana.');
    } finally {
      setCargando(false);
    }
  }, [desde, hasta, seleccion, soloFalta]);

  useEffect(() => {
    if (!ninguno) cargar();
  }, [cargar, ninguno]);

  // La URL del aviso al entrar (también si ya estaba abierta la página)
  useEffect(() => {
    if (!location.search) return;
    if (inicial.desde) setDesde(inicial.desde);
    if (inicial.hasta) setHasta(inicial.hasta);
    if (inicial.proveedor) {
      setProveedorFiltro(inicial.proveedor);
      setProveedorExport(inicial.proveedor);
    }
    if (inicial.soloFalta) setSoloFalta(true);
  }, [location.search, inicial]);

  // Avisos de pedidos cortos o sobrantes en los días del rango
  const cargarAvisos = useCallback(async () => {
    if (!desde || !hasta) return;
    try {
      const { data } = await listaCompraV2API.avisosPedidos({ desde, hasta });
      setAvisos(data || null);
    } catch (err) {
      setAvisos(null); // solo informativo: sin aviso si falla
    }
  }, [desde, hasta]);

  useEffect(() => {
    cargarAvisos();
  }, [cargarAvisos]);

  const cambiarRango = (campo, valor) => {
    if (campo === 'desde') setDesde(valor);
    else setHasta(valor);
    setSeleccion(null);
  };

  const idsMarcados = useMemo(() => {
    if (Array.isArray(seleccion)) return seleccion;
    return eventos.map(e => e.presupuesto_id);
  }, [seleccion, eventos]);

  const alternarEvento = (id) => {
    const actual = new Set(idsMarcados);
    if (actual.has(id)) actual.delete(id);
    else actual.add(id);
    setSeleccion(eventos.map(e => e.presupuesto_id).filter(x => actual.has(x)));
  };

  const parametros = () => ({
    desde, hasta, presupuestoIds: idsMarcados.length > 0 ? idsMarcados : null, proveedorId: proveedorExport || null,
  });

  const idsDeLaCompra = () => datos?.presupuesto_ids || idsMarcados;

  // "No pedir" (a cada evento del rango) y cantidades a mano (solo este rango);
  // se recarga para que el servidor recalcule coste y totales
  const guardarAjuste = async (linea, cambios) => {
    setGuardando(prev => ({ ...prev, [linea.clave]: true }));
    try {
      const { data } = await listaCompraV2API.combinadaGuardarLinea(linea.ingrediente_id, {
        presupuesto_ids: idsDeLaCompra(), desde, hasta, ...cambios,
      });
      if (Number(data?.eventos_omitidos) > 0) {
        toast({
          title: 'Algunos eventos no se han tocado',
          description: `${data.eventos_omitidos} ${data.eventos_omitidos === 1 ? 'evento ya celebrado o cancelado' : 'eventos ya celebrados o cancelados'}: su lista no cambia.`,
        });
      }
      await cargar();
    } catch (err) {
      toast({ title: 'No se pudo guardar la línea', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setGuardando(prev => {
        const r = { ...prev };
        delete r[linea.clave];
        return r;
      });
    }
  };

  // PDF y WhatsApp del pedido de un proveedor (cantidades vigentes, sin "No pedir")
  // Con "Solo lo que falta": el pedido complementario (solo la diferencia sobre lo ya pedido)
  const pdfProveedor = async (grupo, fecha, complementario = soloFalta) => {
    try {
      const respuesta = await listaCompraV2API.combinadaPdf({
        desde, hasta, presupuestoIds: idsMarcados.length > 0 ? idsMarcados : null, proveedorId: grupo.proveedorId, fechaEntrega: fecha,
        soloFalta: complementario,
      });
      const nombre = String(grupo.titulo || 'proveedor').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      descargarRespuesta(respuesta, `${complementario ? 'pedido_complementario' : 'pedido_semana'}_${desde}_${nombre}`);
      return true;
    } catch (err) {
      toast({ title: 'No se pudo descargar el PDF', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
      return false;
    }
  };

  const whatsappProveedor = async (grupo, fecha, complementario = soloFalta) => {
    try {
      const { data } = await listaCompraV2API.combinadaWhatsapp({
        desde, hasta, presupuestoIds: idsMarcados.length > 0 ? idsMarcados : null, proveedorId: grupo.proveedorId, fechaEntrega: fecha,
        soloFalta: complementario,
      });
      if (!data?.texto) {
        toast({ title: 'Nada que pedir', description: `No hay líneas que pedir a ${grupo.titulo}.` });
        return false;
      }
      // Móvil: compartir o WhatsApp directamente; escritorio: portapapeles
      const movil = await compartirTextoMovil(data.texto);
      if (movil) return movil !== 'cancelado';
      const ok = await copiarTexto(data.texto);
      toast(ok
        ? { title: 'Texto copiado', description: `Pedido a ${grupo.titulo}: pégalo en WhatsApp.` }
        : { title: 'No se pudo copiar', description: 'Tu navegador no deja copiar al portapapeles.', variant: 'destructive' });
      return ok;
    } catch (err) {
      toast({ title: 'No se pudo preparar el texto', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
      return false;
    }
  };

  // Con "Solo lo que falta" (o desde el aviso), el pedido complementario se suma a lo ya pedido
  const marcarPedidas = async (grupo, fecha = null, complementario = soloFalta) => {
    try {
      const { data } = await listaCompraV2API.combinadaMarcarPedidas({
        presupuesto_ids: idsDeLaCompra(), desde, hasta, proveedor_id: grupo.proveedorId,
        ...(fecha ? { fecha_entrega: fecha } : {}), ...(complementario ? { solo_falta: true } : {}),
      });
      toast({ title: 'Líneas marcadas', description: data?.message || 'Marcadas como pedidas.' });
      await cargar();
      cargarAvisos();
    } catch (err) {
      toast({ title: 'No se pudieron marcar', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  // Estado de una línea (pendiente / pedido / comprado) en todos los eventos del rango
  const cambiarEstado = async (linea, estado) => {
    setGuardando(prev => ({ ...prev, [linea.clave]: true }));
    try {
      await listaCompraV2API.combinadaEstadoLinea(linea.ingrediente_id, {
        presupuesto_ids: idsDeLaCompra(), desde, hasta, estado,
      });
      await cargar();
      cargarAvisos();
    } catch (err) {
      toast({ title: 'No se pudo cambiar el estado', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setGuardando(prev => {
        const r = { ...prev };
        delete r[linea.clave];
        return r;
      });
    }
  };

  // "Todo pedido" / "Todo comprado" de un proveedor
  const estadoProveedor = async (grupo, estado) => {
    try {
      const { data } = await listaCompraV2API.combinadaEstadoProveedor({
        presupuesto_ids: idsDeLaCompra(), desde, hasta, proveedor_id: grupo.proveedorId, estado,
      });
      toast({ title: 'Líneas marcadas', description: data?.message || (estado === 'pedido' ? 'Marcadas como pedidas.' : 'Marcadas como compradas.') });
      await cargar();
      cargarAvisos();
    } catch (err) {
      toast({ title: 'No se pudieron marcar', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const descargarPdf = async () => {
    setOcupado(true);
    try {
      const respuesta = await listaCompraV2API.combinadaPdf(parametros());
      descargarRespuesta(respuesta, `compra_semana_${desde}_${hasta}`);
    } catch (err) {
      toast({ title: 'No se pudo descargar el PDF', description: err.message, variant: 'destructive' });
    } finally {
      setOcupado(false);
    }
  };

  const copiarWhatsapp = async () => {
    setOcupado(true);
    try {
      const { data } = await listaCompraV2API.combinadaWhatsapp(parametros());
      if (!data?.texto) {
        toast({ title: 'Nada que pedir', description: 'No hay líneas con falta de compra.' });
        return;
      }
      const movil = await compartirTextoMovil(data.texto);
      if (movil) return;
      const ok = await copiarTexto(data.texto);
      toast(ok
        ? { title: 'Texto copiado', description: `${(data.mensajes || []).length} ${(data.mensajes || []).length === 1 ? 'proveedor' : 'proveedores'}: pégalo en WhatsApp.` }
        : { title: 'No se pudo copiar', description: 'Tu navegador no deja copiar al portapapeles.', variant: 'destructive' });
    } catch (err) {
      toast({ title: 'No se pudo preparar el texto', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setOcupado(false);
    }
  };

  // La propuesta según el proveedor (días de entrega y antelación) o, si no
  // viene, la víspera de la primera fecha
  const entregaPedido = pedido?.grupo?.proveedorId ? (datos?.totales_proveedor?.[pedido.grupo.proveedorId] || null) : null;
  const fechaEntregaInicial = useMemo(() => {
    if (entregaPedido?.fecha_propuesta) return entregaPedido.fecha_propuesta;
    const fechas = (pedido?.grupo?.lineas || []).filter(l => l.pedible)
      .flatMap(l => (l.desglose || []).map(d => d.fecha));
    return vispera(fechas.length > 0 ? fechas : [desde], hoyMadrid());
  }, [pedido, desde, entregaPedido]);

  const crearOrden = async (fecha, entregas = null) => {
    if (!pedido?.grupo?.proveedorId) return;
    setPedido(prev => ({ ...prev, creando: true }));
    try {
      const { data } = await listaCompraV2API.crearOrdenCombinada(pedido.grupo.proveedorId, {
        presupuesto_ids: datos?.presupuesto_ids || idsMarcados,
        desde,
        hasta,
        fecha_entrega: fecha,
        ...(entregas ? { entregas } : {}),
      });
      setPedido(prev => ({ ...prev, creando: false, resultado: data }));
      cargar();
    } catch (err) {
      setPedido(prev => ({ ...prev, creando: false }));
      toast({ title: 'No se pudo crear la orden', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const lineas = useMemo(() => (ninguno ? [] : (datos?.lineas || [])), [datos, ninguno]);
  // Lo que se enseña: el proveedor de "Ver la diferencia" y, con "Solo lo que
  // falta", se puede pedir lo que falta aunque la línea ya esté pedida
  const lineasVisibles = useMemo(() => {
    let filtradas = proveedorFiltro ? lineas.filter(l => l.proveedor_id === proveedorFiltro) : lineas;
    if (estadoFiltro === 'no_pedir') filtradas = filtradas.filter(l => l.no_pedir);
    else if (estadoFiltro) filtradas = filtradas.filter(l => !l.no_pedir && l.estado === estadoFiltro);
    if (!soloFalta) return filtradas;
    return filtradas.map(l => ({ ...l, pedible: !l.no_pedir && Number(l.diferencia_paquetes) > 0 }));
  }, [lineas, proveedorFiltro, soloFalta, estadoFiltro]);
  const nombreFiltro = proveedorFiltro
    ? (lineas.find(l => l.proveedor_id === proveedorFiltro)?.proveedor_nombre
      || (avisos?.proveedores || []).find(p => p.proveedor_id === proveedorFiltro)?.proveedor_nombre || 'el proveedor')
    : '';

  // Franja de avisos: por proveedor
  const grupoDeAviso = (p) => ({ proveedorId: p.proveedor_id, titulo: p.proveedor_nombre });
  const verDiferencia = (p) => {
    setProveedorFiltro(p.proveedor_id);
    setProveedorExport(p.proveedor_id);
    setSoloFalta(true);
  };
  const marcarRevisado = async (p) => {
    try {
      await listaCompraV2API.avisosPedidosRevisado({ proveedor_id: p.proveedor_id, huella: p.huella });
      toast({ title: 'Aviso revisado', description: `${p.proveedor_nombre}: no se vuelve a avisar hasta que cambien las cifras.` });
      await cargarAvisos();
      return true;
    } catch (err) {
      toast({ title: 'No se pudo marcar como revisado', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
      return false;
    }
  };
  const proveedores = useMemo(() => {
    const mapa = new Map();
    lineas.forEach((l) => {
      if (l.proveedor_id && !mapa.has(l.proveedor_id)) mapa.set(l.proveedor_id, l.proveedor_nombre);
    });
    return Array.from(mapa.entries()).sort((a, b) => String(a[1]).localeCompare(String(b[1]), 'es'));
  }, [lineas]);

  const marcados = new Set(idsMarcados);
  const totalesEstado = datos?.totales_estado || {};
  // Chips de totales por estado (En stock solo si hay)
  const chipsEstado = [
    ['pendiente', 'Pendiente'], ['pedido', 'Pedido'], ['comprado', 'Comprado'],
    ...(Number(totalesEstado.en_stock) > 0 || estadoFiltro === 'en_stock' ? [['en_stock', 'En stock']] : []),
    ['no_pedir', 'No pedir'],
  ];

  return (
    <div className="flex flex-col gap-4" data-testid="compra-semana">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-extrabold text-[#16221D]">Compra de la semana</h2>
        <p className="text-xs text-[#5B6B64]">
          Une la compra de los eventos confirmados del rango: cada ingrediente sumado, con el stock y las reservas contadas una sola vez y lo ya pedido restado. El estado que cambies aquí se aplica a la línea de cada evento del rango.
        </p>
        <p className="text-xs text-[#5B6B64]" data-testid="compra-semana-ayuda-ajustes">
          "No pedir" se guarda en la lista de cada evento. Los ajustes de cantidad se guardan para este rango de fechas.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs font-semibold text-[#4A5A53]">
          Desde
          <input type="date" value={desde} onChange={(e) => cambiarRango('desde', e.target.value)} className={claseInput} data-testid="compra-semana-desde" />
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-[#4A5A53]">
          Hasta
          <input type="date" value={hasta} onChange={(e) => cambiarRango('hasta', e.target.value)} className={claseInput} data-testid="compra-semana-hasta" />
        </label>
        <button type="button" onClick={cargar} disabled={cargando || ninguno} className={botonSecundario} data-testid="compra-semana-actualizar">
          {cargando ? 'Calculando...' : 'Actualizar'}
        </button>
      </div>

      {error && <p className="text-sm text-[#8A1C12]" data-testid="compra-semana-error">{error}</p>}

      <AvisosPedidosFranja
        avisos={avisos}
        onVerDiferencia={verDiferencia}
        onPdf={(p) => pdfProveedor(grupoDeAviso(p), null, true)}
        onWhatsapp={(p) => whatsappProveedor(grupoDeAviso(p), null, true)}
        onMarcarComplementario={(p) => marcarPedidas(grupoDeAviso(p), null, true)}
        onRevisado={marcarRevisado}
      />

      <div className="rounded-[12px] border border-[#E1E6E3] px-3 py-2.5" data-testid="compra-semana-eventos">
        <div className="text-xs font-bold uppercase tracking-wide text-[#5B6B64] mb-1">
          Eventos del rango ({eventos.length})
        </div>
        {eventos.length === 0 ? (
          <p className="text-sm text-[#5B6B64]">{cargando ? 'Buscando eventos...' : 'No hay eventos confirmados en estas fechas.'}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {eventos.map(e => (
              <li key={e.presupuesto_id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 py-1.5 sm:py-0 text-[13px]">
                <label className="flex items-center gap-2 min-w-0">
                  <input
                    type="checkbox"
                    checked={marcados.has(e.presupuesto_id)}
                    onChange={() => alternarEvento(e.presupuesto_id)}
                    className="w-5 h-5 sm:w-auto sm:h-auto shrink-0"
                    data-testid={`compra-semana-evento-${e.presupuesto_id}`}
                  />
                  <span className="font-semibold text-[#16221D]">{e.numero}</span>
                  <span className="truncate text-[#4A5A53]">{e.nombre_evento}</span>
                </label>
                <span className="text-[12px] text-[#5B6B64]">
                  {(e.fechas || []).map(ddmm).join(', ')} · {e.personas} pax
                  {e.multievento ? ` · ${(e.jornadas || []).map(j => j.etiqueta).filter(Boolean).join(', ')}` : ''}
                </span>
                {e.jornadas_vacias && <span className="px-1.5 rounded text-[11px] font-bold bg-[#FDF0DC] text-[#92400E]">Jornadas sin líneas</span>}
                {e.estado !== 'confirmado' && <span className="px-1.5 rounded text-[11px] font-bold bg-[#EEF1EF] text-[#4A5A53]">{e.estado}</span>}
                {verPresupuesto && (
                  <Link to={`/presupuestos/${e.presupuesto_id}`} className="py-1.5 px-1 inline-block text-[12px] font-semibold text-[#11704F] hover:underline">Abrir</Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {ninguno ? (
        <p className="text-sm text-[#5B6B64]">Marca al menos un evento.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-[#CBD3CF] overflow-hidden" role="group" aria-label="Agrupar">
              {[['proveedor', 'Por proveedor'], ['seccion', 'Por sección']].map(([valor, texto]) => (
                <button
                  key={valor}
                  type="button"
                  onClick={() => setAgrupacion(valor)}
                  aria-pressed={agrupacion === valor}
                  className={`h-10 sm:h-[34px] px-3 text-[13px] font-bold ${agrupacion === valor ? 'bg-[#E3F3EC] text-[#0F5E43]' : 'bg-white text-[#16221D] hover:bg-gray-50'}`}
                >
                  {texto}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1.5 py-1.5 text-[13px] text-[#4A5A53]">
              <input type="checkbox" checked={soloFalta} onChange={(e) => setSoloFalta(e.target.checked)} className="w-5 h-5 sm:w-auto sm:h-auto" data-testid="compra-semana-solo-falta" />
              Solo lo que falta
            </label>
            {proveedorFiltro && (
              <span className="flex items-center gap-1.5 px-2 h-[28px] rounded-lg bg-[#E3F3EC] text-[12px] font-bold text-[#0F5E43]" data-testid="compra-semana-filtro-proveedor">
                Solo {nombreFiltro}
                <button type="button" onClick={() => setProveedorFiltro('')} className="font-semibold underline">Ver todos</button>
              </span>
            )}
            {/* En móvil el selector ocupa su línea (con la opción más larga desbordaba la página) */}
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-auto min-w-0">
              <select
                value={proveedorExport}
                onChange={(e) => setProveedorExport(e.target.value)}
                className={`${claseInput} w-full sm:w-auto max-w-full`}
                aria-label="Proveedor para exportar"
              >
                <option value="">Todos los proveedores</option>
                {proveedores.map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
              </select>
              <button type="button" onClick={descargarPdf} disabled={ocupado || lineas.length === 0} className={botonSecundario} data-testid="compra-semana-pdf">PDF</button>
              <button type="button" onClick={copiarWhatsapp} disabled={ocupado || lineas.length === 0} className={botonPrincipal} data-testid="compra-semana-whatsapp">WhatsApp</button>
            </div>
          </div>

          {datos && (
            <div className="flex flex-wrap items-center gap-1.5 text-[13px]" data-testid="compra-semana-totales">
              {chipsEstado.map(([clave, texto]) => {
                const activo = estadoFiltro === clave;
                return (
                  <button
                    key={clave}
                    type="button"
                    onClick={() => setEstadoFiltro(activo ? '' : clave)}
                    aria-pressed={activo}
                    className={`h-9 sm:h-[28px] px-2 rounded-lg border text-[12px] font-bold whitespace-nowrap ${activo
                      ? 'bg-[#E3F3EC] border-duetto-primary text-[#0F5E43]'
                      : `border-transparent ${TONO_ESTADO[clave] || 'bg-[#EEF1EF] text-[#4A5A53]'} hover:border-[#CBD3CF]`}`}
                    title={activo ? 'Quitar el filtro' : `Ver solo las líneas en ${texto.toLowerCase()}`}
                    data-testid={`compra-semana-filtro-${clave}`}
                  >
                    {texto} {Number(totalesEstado[clave]) || 0}
                  </button>
                );
              })}
              <span className="font-extrabold text-[#16221D]" data-testid="compra-semana-falta-por-pedir">
                Falta por pedir: {Number(totalesEstado.con_falta) || 0} {Number(totalesEstado.con_falta) === 1 ? 'línea' : 'líneas'} · {formatEuros(totalesEstado.coste_falta)}
              </span>
              {Number(datos.total_coste) > 0 && (
                <span className="font-extrabold text-[#16221D]">· compra estimada {formatEuros(datos.total_coste)} sin IVA</span>
              )}
              {estadoFiltro && (
                <button type="button" onClick={() => setEstadoFiltro('')} className="text-[12px] font-semibold text-[#11704F] underline" data-testid="compra-semana-filtro-ver-todas">
                  Ver todas
                </button>
              )}
            </div>
          )}
          {(datos?.avisos || []).length > 0 && (
            <div className="rounded-[10px] border px-3.5 py-2 text-sm bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]">
              {datos.avisos.map(a => <div key={a.texto}>{a.texto}</div>)}
            </div>
          )}

          {lineasVisibles.length === 0 ? (
            <p className="text-sm text-[#5B6B64]" data-testid="compra-semana-vacia">{cargando ? 'Calculando...' : (estadoFiltro ? 'Ninguna línea en ese estado.' : 'Nada que comprar para estos eventos.')}</p>
          ) : (
            <TablaListaCompra
              lineas={lineasVisibles}
              secciones={datos?.secciones || []}
              estados={ESTADOS_SEMANA}
              agrupacion={agrupacion}
              multievento
              soloLectura
              guardando={guardando}
              onCambiarEstado={cambiarEstado}
              onEstadoProveedor={estadoProveedor}
              onNoPedir={(l, valor) => guardarAjuste(l, { no_pedir: valor })}
              onCambiarCantidad={guardarAjuste}
              onPdfProveedor={pdfProveedor}
              onWhatsappProveedor={whatsappProveedor}
              onMarcarPedidas={marcarPedidas}
              onPedir={MOSTRAR_CREAR_ORDEN ? (grupo) => setPedido({ grupo, creando: false, resultado: null }) : null}
              onAbrirIngrediente={setIngredienteAbierto}
              totalCoste={datos?.total_coste}
              notaCoste={datos?.nota_coste}
              totalesProveedor={datos?.totales_proveedor}
              prefijoTestid="compra-semana"
            />
          )}
        </>
      )}

      <ConfirmarPedido
        abierto={!!pedido}
        grupo={pedido?.grupo}
        fechaInicial={fechaEntregaInicial}
        entrega={entregaPedido}
        creando={!!pedido?.creando}
        resultado={pedido?.resultado}
        onConfirmar={crearOrden}
        onCerrar={() => setPedido(null)}
      />

      <IngredienteModal
        abierto={!!ingredienteAbierto}
        ingredienteId={ingredienteAbierto}
        onCerrar={() => setIngredienteAbierto(null)}
        onGuardado={() => cargar()}
      />
      {/* Sin él, los avisos (texto copiado, errores) no se veían en Compras y Almacén */}
      <Toaster />
    </div>
  );
};

export default CompraSemana;
